import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Col, Container, FormGroup, Input, Label, Modal, ModalBody, Row } from 'reactstrap';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { VLDatePicker } from 'vl-common/src/components/VLDatePicker';
import useApi from 'vl-common/src/hooks/useApi';
import { tools } from 'vl-common/src/lib/service.tools';
import { AlertMessage } from 'vl-common/src/components/AlertMessage/AlertMessage';
import { LoadingButton } from 'vl-common/src/components/LoadingButton';
import { PostcodeAutocomplete } from 'vl-common/src/components/PostcodeAutocomplete';
import format from 'vl-common/src/lib/formatDate';
import { differenceInYears } from 'date-fns';
import { sanitiseValueFromAngleBrackets } from 'vl-common/src/utils';

const MINIMUM_AGE = 18;

const initialForm = {
  new_user_guid: '',
  membership_no: '',
  title: '',
  first_name: '',
  last_name: '',
  email: '',
  dob: '0000-00-00',
  sex: '',
  tel_mobile_no: '',
  tel_other_no: '',
  pref_tel: 'O',
  avatar_url: '',
  addline1: '',
  addline2: '',
  addline3: '',
  addline4: '',
  region: '',
  postcode: '',
  country: ''
};

const AdditionalPatientInfo = ({ close, memberExternal, show, selectedPatient }: any) => {
  const { t } = useTranslation('common');
  const { Api, consumed } = useApi();
  const queryClient = useQueryClient();
  const [activeField, setActiveField] = useState<boolean>(false);
  const [formData, setFormData] = useState<typeof initialForm>(initialForm);
  const [alternativeContactNumber, setAlternativeContactNumber] = useState<string>('');
  const [dpDate, setDpDate] = useState<Date | null>(null);
  const [showAlertMessage, setShowAlertMessage] = useState<{ visibility: boolean; msg: string }>({
    visibility: false,
    msg: ''
  });
  const [isCreatingPatient, setIsCreatingPatient] = useState<boolean>(false);

  useEffect(() => {
    if (!memberExternal) return;

    setFormData(memberExternal);
    setDpDate(new Date(memberExternal.dob));
    setAlternativeContactNumber(memberExternal.tel_other_no.replace(' ', '').trim());
    setActiveField(true);
  }, [memberExternal]);

  const handleCancel = () => close(true);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!isFormValid()) return;
    setIsCreatingPatient(true);

    const currentPatient = selectedPatient;
    const data = {
      up_user_guid: currentPatient.user_guid,
      title: currentPatient.title,
      first_name: currentPatient.first_name,
      last_name: currentPatient.last_name,
      email: currentPatient.email,
      tel_mobile: currentPatient.tel_mobile_no,
      tel_other: alternativeContactNumber,
      tel_pref: currentPatient.tel_pref_no === 'mobile' ? 'M' : 'O',
      dob: format(dpDate, 'yyyy-MM-dd'),
      sex: formData.sex,
      mem_no: currentPatient.membership_no,
      addline1: formData.addline1,
      addline2: formData.addline2,
      addline3: formData.addline3,
      addline4: formData.addline4,
      region: formData.region,
      postcode: formData.postcode,
      country: formData.country
    };

    Api.referrer
      .member_editProfile(data)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['patientDetails', currentPatient.user_guid] });
        setIsCreatingPatient(false);
        close();
      })
      .catch((e) => {
        const {
          data: { message = 'Bad Request, try again.' }
        } = e.response;

        setIsCreatingPatient(false);
        displayAlertMessage(message);
        consumed(e);
      });
  };

  const onSelect_postcode = (recordObject) => {
    const addressFound = recordObject.isAddressFound;
    const field = (propName) => (addressFound ? (recordObject?.[propName] ?? '') : '');

    setFormData({
      ...formData,
      addline1: field('line1'),
      addline2: field('line2'),
      addline3: field('line3'),
      addline4: field('town'),
      region: field('region'),
      country: field('country'),
      postcode: recordObject.postcode
    });
    setActiveField(true);

    if (!addressFound) displayAlertMessage(recordObject.value);
  };

  const handleSanitisedChange = (fieldName: keyof typeof initialForm) => (event) =>
    setFormData((prev) => ({ ...prev, [fieldName]: sanitiseValueFromAngleBrackets(event.target.value) }));

  useEffect(() => {
    if (dpDate && !isAgeLegal()) {
      displayAlertMessage('DOB indicates that user is below 18, we are sorry but you cannot proceed');
    }
  }, [dpDate]);

  const displayAlertMessage = (msg = '') => {
    setShowAlertMessage({ visibility: true, msg });
    const t = setTimeout(() => {
      setShowAlertMessage({ visibility: false, msg });
      clearTimeout(t);
    }, 7000);
  };

  const isAgeLegal = () => differenceInYears(new Date(), dpDate) >= MINIMUM_AGE;

  const isFormValid = () => {
    if (!isAgeLegal()) {
      displayAlertMessage(
        dpDate === null
          ? 'Please select date of birth'
          : 'DOB indicates that user is below 18, we are sorry but you cannot proceed'
      );
      return false;
    } else if (tools.isEmptyOrNull(formData.sex)) {
      displayAlertMessage('Please select the sex');
      return false;
    } else if (tools.isEmptyOrNull(formData.addline1)) {
      displayAlertMessage('Please enter address line 1');
      return false;
    } else if (tools.isEmptyOrNull(formData.addline4)) {
      displayAlertMessage('Please enter the town');
      return false;
    } else if (tools.isEmptyOrNull(formData.region)) {
      displayAlertMessage('Please enter the county');
      return false;
    } else if (tools.isEmptyOrNull(formData.postcode)) {
      displayAlertMessage('Please enter in a postcode');
      return false;
    } else {
      return true;
    }
  };

  const idleLabel = memberExternal ? 'Save' : 'Update';
  const busyLabel = memberExternal ? 'Saving...' : 'Updating...';
  const submitButtonLabel = isCreatingPatient ? busyLabel : idleLabel;

  return (
    <Modal
      centered
      className="vl-add-modal addm-modal"
      size="lg"
      isOpen={show}
      toggle={handleCancel}
      backdrop="static"
      keyboard={false}
    >
      <ModalBody className="p-5">
        <Container fluid>
          <Row>
            <Col xs="12">
              <span className="vl-modal-title">{t('Additional')}</span>
              <span className="addm-member">{t('Patient Info')}</span>
            </Col>
            <AlertMessage message={showAlertMessage.msg} visibility={showAlertMessage.visibility} />
            <br />
            <Col xs="12">
              <form className="mt-5" onSubmit={handleSubmit}>
                <Row className="addm-container">
                  <Col xs="6">
                    <FormGroup>
                      <Label for="ContactNumber" style={{ fontSize: '15px', marginBottom: '15px' }}>
                        {t('Secondary Contact Number')}
                      </Label>
                      <PhoneInput
                        placeholder={t('Enter phone number')}
                        defaultCountry="GB"
                        value={alternativeContactNumber}
                        onChange={(value) => {
                          setAlternativeContactNumber(value);
                        }}
                      />
                    </FormGroup>
                  </Col>
                  <Col xs="3">
                    <FormGroup>
                      <Label for="sex">
                        {t('Sex')}
                        <span className="vl-product-satsuma">*</span>
                      </Label>
                      <div className="addm-select">
                        <Input
                          id="sex"
                          name="sex"
                          type="select"
                          style={{ minWidth: '80px' }}
                          value={formData.sex}
                          required
                          onChange={(event) =>
                            setFormData({
                              ...formData,
                              sex: event.target.value
                            })
                          }
                        >
                          <option disabled value="">
                            {t('Select')}
                          </option>
                          <option value="M">{t('Male')}</option>
                          <option value="F">{t('Female')}</option>
                        </Input>
                      </div>
                    </FormGroup>
                  </Col>
                  <Col xs="3">
                    <FormGroup>
                      <Label for="dob" style={{ marginBottom: '10px' }}>
                        {t('Date of Birth')}
                        <span className="vl-product-satsuma">*</span>
                      </Label>
                      <VLDatePicker
                        className="vl-width-full"
                        selected={dpDate}
                        onChange={setDpDate}
                        peekNextMonth
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        placeholderText="00/00/0000"
                      />
                    </FormGroup>
                  </Col>
                  <Col xs="6">
                    <FormGroup>
                      <Label for="postcode">
                        {t('Postcode')}
                        <span className="vl-product-satsuma">*</span>
                      </Label>
                      <PostcodeAutocomplete
                        placeholder={t('Enter Postcode')}
                        valueEx={formData.postcode}
                        onSelect={onSelect_postcode}
                      />
                    </FormGroup>
                  </Col>

                  <Col
                    xs="6"
                    className={classnames({
                      'addm-opacity': !activeField
                    })}
                  >
                    <FormGroup>
                      <Label for="addline1">
                        {t('Address Line 1')}
                        <span className="vl-product-satsuma">*</span>
                      </Label>
                      <Input
                        id="addline1"
                        name="addline1"
                        type="text"
                        autoComplete="line1"
                        value={formData.addline1}
                        onChange={handleSanitisedChange('addline1')}
                        placeholder={t('Address Line 1')}
                        readOnly={!activeField}
                      />
                    </FormGroup>
                  </Col>
                  <Col
                    xs="6"
                    className={classnames({
                      'addm-opacity': !activeField
                    })}
                  >
                    <FormGroup>
                      <Label for="addline2">{t('Address Line 2')}</Label>
                      <Input
                        className="addm-gray-ph"
                        type="text"
                        onChange={handleSanitisedChange('addline2')}
                        value={formData.addline2}
                        placeholder={t('Enter Address Line 2')}
                      />
                    </FormGroup>
                  </Col>
                  <Col
                    xs="6"
                    className={classnames({
                      'addm-opacity': !activeField
                    })}
                  >
                    <FormGroup>
                      <Label for="addline3">{t('Address Line 3')}</Label>
                      <Input
                        className="addm-gray-ph"
                        type="text"
                        onChange={handleSanitisedChange('addline3')}
                        value={formData.addline3}
                        placeholder={t('Enter Address Line 3')}
                      />
                    </FormGroup>
                  </Col>
                  <Col
                    xs="6"
                    className={classnames({
                      'addm-opacity': !activeField
                    })}
                  >
                    <FormGroup>
                      <Label for="region">
                        {t('Town/City')}
                        <span className="vl-product-satsuma">*</span>
                      </Label>
                      <Input
                        id="town"
                        name="town"
                        type="text"
                        autoComplete="town"
                        value={formData.addline4}
                        onChange={handleSanitisedChange('addline4')}
                        placeholder={t('Town/City')}
                        readOnly={!activeField}
                      />
                    </FormGroup>
                  </Col>
                  <Col
                    xs="6"
                    className={classnames({
                      'addm-opacity': !activeField
                    })}
                  >
                    <FormGroup>
                      <Label for="country">
                        {t('County')}
                        <span className="vl-product-satsuma">*</span>
                      </Label>
                      <Input
                        id="county"
                        name="county"
                        type="text"
                        autoComplete="county"
                        value={formData.region}
                        onChange={handleSanitisedChange('region')}
                        placeholder={t('County')}
                        readOnly={!activeField}
                      />
                    </FormGroup>
                  </Col>

                  <Col xs="12" className="vl-flex-row-end">
                    <div>
                      <Button
                        className="vl-button vl-button-gray vl-font-regular"
                        type="button"
                        onClick={handleCancel}
                        data-testid="additional-patient-info-cancel"
                      >
                        {t('Cancel')}
                      </Button>

                      <LoadingButton
                        className="vl-button vl-font-regular"
                        type="submit"
                        loading={isCreatingPatient}
                        disabled={isCreatingPatient}
                        data-testid="additional-patient-info-submit"
                      >
                        {submitButtonLabel}
                      </LoadingButton>
                    </div>
                  </Col>
                </Row>
              </form>
            </Col>
          </Row>
        </Container>
      </ModalBody>
    </Modal>
  );
};

export default AdditionalPatientInfo;
